(function () {
  if (window.SteyV11Experience?.loaded) {
    window.SteyV11Experience.init();
    return;
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const openingConfig = window.SteyOpeningConfig || {};
  const railCleanups = new WeakMap();

  const initReveal = (root = document) => {
    const items = Array.from(root.querySelectorAll('[data-stey-v11-reveal]:not([data-stey-v11-ready])'));
    if (!items.length) return;

    items.forEach((item, index) => {
      item.dataset.steyV11Ready = 'true';
      item.style.setProperty('--stey-v11-delay', `${Math.min(index * 70, 420)}ms`);
    });

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    items.forEach((item) => observer.observe(item));
  };

  const initRails = (root = document) => {
    root.querySelectorAll('[data-stey-v11-rail]:not([data-stey-v11-rail-ready])').forEach((rail) => {
      rail.dataset.steyV11RailReady = 'true';
      const section = rail.closest('[data-stey-v11-section]');
      const progress = section?.querySelector('[data-stey-v11-progress]');
      let isDown = false;
      let startX = 0;
      let startScroll = 0;
      let moved = false;

      const updateProgress = () => {
        if (!progress) return;
        const max = rail.scrollWidth - rail.clientWidth;
        const ratio = max > 0 ? rail.scrollLeft / max : 0;
        progress.style.transform = `scaleX(${Math.max(0.08, Math.min(1, ratio || 0.08))})`;
      };

      const endDrag = () => {
        isDown = false;
        rail.classList.remove('is-dragging');
        window.setTimeout(() => {
          moved = false;
        }, 0);
      };

      const onPointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        isDown = true;
        moved = false;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        rail.classList.add('is-dragging');
        rail.setPointerCapture?.(event.pointerId);
      };

      const onPointerMove = (event) => {
        if (!isDown) return;
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 4) moved = true;
        rail.scrollLeft = startScroll - delta;
      };

      const onClick = (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
      };

      rail.addEventListener('pointerdown', onPointerDown);
      rail.addEventListener('pointermove', onPointerMove);
      rail.addEventListener('pointerup', endDrag);
      rail.addEventListener('pointercancel', endDrag);
      rail.addEventListener('mouseleave', endDrag);
      rail.addEventListener('click', onClick, true);
      rail.addEventListener('scroll', updateProgress, { passive: true });
      window.addEventListener('resize', updateProgress, { passive: true });
      railCleanups.set(rail, () => window.removeEventListener('resize', updateProgress));
      window.requestAnimationFrame(updateProgress);
    });
  };

  const initOpening = () => {
    const overlay = document.querySelector('[data-stey-opening]');
    if (!overlay) {
      document.documentElement.classList.remove('stey-opening-lock');
      document.documentElement.classList.add('stey-opening-skip');
      return;
    }

    if (overlay.dataset.steyOpeningReady === 'true') return;

    overlay.dataset.steyOpeningReady = 'true';
    const firstVisitOnly = overlay.dataset.steyOpeningFirstVisit !== 'false' && openingConfig.firstVisitOnly !== false;
    const minVisible = Number(overlay.dataset.steyOpeningDuration || openingConfig.duration || 2100);

    const getSeen = () => {
      try {
        return sessionStorage.getItem('stey_opening_seen') === 'true';
      } catch (error) {
        return false;
      }
    };
    const setSeen = () => {
      try {
        sessionStorage.setItem('stey_opening_seen', 'true');
      } catch (error) {
        return false;
      }
      return true;
    };

    if (openingConfig.enabled === false || prefersReducedMotion || (firstVisitOnly && getSeen())) {
      overlay.remove();
      document.documentElement.classList.remove('stey-opening-lock');
      document.documentElement.classList.add('stey-opening-skip');
      return;
    }

    const startedAt = window.SteyOpeningStartedAt || Date.now();
    let hasLeft = false;

    const leave = () => {
      if (hasLeft) return;
      hasLeft = true;
      overlay.classList.add('is-leaving');
      document.documentElement.classList.remove('stey-opening-lock');
      if (firstVisitOnly) setSeen();
    };

    const scheduleLeave = () => {
      const elapsed = Date.now() - startedAt;
      window.setTimeout(leave, Math.max(0, minVisible - elapsed));
    };

    if (document.readyState === 'complete') scheduleLeave();
    else window.addEventListener('load', scheduleLeave, { once: true });

    overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
  };

  const initGiftFinders = (root = document) => {
    root.querySelectorAll('[data-stey-gift-finder]:not([data-stey-gift-ready])').forEach((finder) => {
      finder.dataset.steyGiftReady = 'true';
      const options = Array.from(finder.querySelectorAll('[data-stey-gift-option]'));
      const result = finder.querySelector('[data-stey-gift-result]');
      const link = finder.querySelector('[data-stey-gift-link]');
      const groupOrder = ['recipient', 'style', 'budget'];

      const sync = () => {
        const selected = groupOrder
          .map((group) => options.find((option) => option.dataset.group === group && option.classList.contains('is-active')))
          .filter(Boolean);

        if (result) {
          result.textContent = selected.map((option) => option.dataset.label || option.textContent.trim()).join(' · ');
        }

        const linkedSelection = [...selected].reverse().find((option) => option.dataset.url);
        if (link && linkedSelection) link.href = linkedSelection.dataset.url;
      };

      options.forEach((option) => {
        option.addEventListener('click', () => {
          const group = option.dataset.group;
          options
            .filter((candidate) => candidate.dataset.group === group)
            .forEach((candidate) => candidate.classList.toggle('is-active', candidate === option));
          sync();
        });
      });

      sync();
    });
  };

  const initSizeGuides = (root = document) => {
    root.querySelectorAll('[data-stey-size-guide]:not([data-stey-size-guide-ready])').forEach((guide) => {
      guide.dataset.steySizeGuideReady = 'true';
      const modal = guide.querySelector('.stey-size-guide__modal');
      const openButtons = guide.querySelectorAll('[data-stey-size-guide-open]');
      const closeButtons = guide.querySelectorAll('[data-stey-size-guide-close]');
      if (!modal || !openButtons.length) return;

      const open = () => {
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('stey-size-guide-lock');
        document.body.classList.add('stey-size-guide-lock');
        guide.querySelector('.stey-size-guide__close')?.focus({ preventScroll: true });
      };

      const close = () => {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('stey-size-guide-lock');
        document.body.classList.remove('stey-size-guide-lock');
      };

      openButtons.forEach((button) => button.addEventListener('click', open));
      closeButtons.forEach((button) => button.addEventListener('click', close));

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.classList.contains('is-open')) close();
      });
    });
  };

  const initPageTransitions = () => {
    if (window.SteyV11PageTransitionReady) return;
    window.SteyV11PageTransitionReady = true;
    const pageTransitionsEnabled = openingConfig.pageTransitions !== false;

    window.requestAnimationFrame(() => {
      document.documentElement.classList.add('stey-page-ready');
    });

    window.addEventListener('pageshow', () => {
      document.documentElement.classList.remove('stey-page-leaving');
    });

    if (!pageTransitionsEnabled || prefersReducedMotion || document.documentElement.classList.contains('shopify-design-mode')) return;

    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = event.target.closest?.('a[href]');
      if (!anchor || anchor.closest('#stey-cart-drawer')) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;

      const url = new URL(anchor.href, window.location.href);
      const isSamePageHash = url.pathname === window.location.pathname && url.search === window.location.search && url.hash;
      if (url.origin !== window.location.origin || isSamePageHash) return;

      event.preventDefault();
      document.documentElement.classList.add('stey-page-leaving');
      window.setTimeout(() => {
        window.location.href = url.href;
      }, 180);
    });
  };

  const init = (root = document) => {
    initReveal(root);
    initRails(root);
    initGiftFinders(root);
    initSizeGuides(root);
    if (root === document) {
      initOpening();
      initPageTransitions();
    }
  };

  window.SteyV11Experience = {
    loaded: true,
    init,
    cleanupRail(rail) {
      railCleanups.get(rail)?.();
      railCleanups.delete(rail);
    },
  };

  const run = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
